import { useMemo, useState } from "react";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { Tooltip } from "./ui/Tooltip";
import { Dialog, DialogTitle, DialogDescription } from "./ui/Dialog";
import CandidatePicker from "./CandidatePicker";
import LicenseRow from "./LicenseRow";
import type { BucketRow } from "./BucketList";

export interface ChainMemberPreview {
  license_number: string;
  license_type: string | null;
  display_name: string | null;
  gbp_display_name?: string | null;
  dba_name: string | null;
  legal_name: string | null;
  premise_city: string | null;
  premise_street: string | null;
  premise_zip?: string | null;
  status: string | null;
  is_active: boolean;
  in_territory: boolean;
}

export interface ChainMatchPayload {
  chain_id: string;
  display_name: string;
  canonical_root: string;
  website: string | null;
  member_count: number;
  member_breakdown: { c10: number; c9: number; c12: number };
  members: ChainMemberPreview[];
  default_selected: string[];
  trigger: "license_chain_id" | "distributor_chain_id" | "name_canonical_root";
  source_distributor_license?: string;
}

interface Props {
  row: BucketRow;
  payload: ChainMatchPayload;
  onConfirmChain: (
    row: BucketRow,
    payload: ChainMatchPayload,
    selectedLicenseNumbers: string[]
  ) => Promise<{ chain_action_id: string; confirmed_count: number }>;
  onPickDifferent: (row: BucketRow, licenseNumber: string) => Promise<void>;
  onMarkDeliveryPartner: (row: BucketRow) => void;
  onNotARetailer: (row: BucketRow) => Promise<void>;
  onSkip: (row: BucketRow) => Promise<void>;
}

const CLASS_LABEL: Record<string, { key: "c10" | "c9" | "c12"; label: string }> = {
  "Commercial -  Retailer": { key: "c10", label: "STOREFRONT (C10)" },
  "Commercial -  Retailer - Non-Storefront": { key: "c9", label: "DELIVERY (C9)" },
  "Commercial -  Microbusiness": { key: "c12", label: "MICROBUSINESS (C12)" },
};

function classKey(t: string | null): "c10" | "c9" | "c12" | "other" {
  if (!t) return "other";
  return CLASS_LABEL[t]?.key ?? "other";
}

function triggerExplain(payload: ChainMatchPayload): string {
  switch (payload.trigger) {
    case "license_chain_id":
      return "This row's resolved license is a known member of an approved chain.";
    case "distributor_chain_id":
      return `This C11 distributor (${payload.source_distributor_license ?? "—"}) is the parent of an approved chain — every member retail license matches.`;
    case "name_canonical_root":
      return `The row name normalizes to '${payload.canonical_root}', the canonical stem of an approved chain.`;
    default:
      return "Matched against an approved chain.";
  }
}

export default function ChainReviewCard({
  row, payload, onConfirmChain, onPickDifferent, onMarkDeliveryPartner, onNotARetailer, onSkip,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [confirmingNotRetailer, setConfirmingNotRetailer] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(payload.default_selected ?? [])
  );

  const grouped = useMemo(() => {
    const out = {
      c10: [] as ChainMemberPreview[],
      c9: [] as ChainMemberPreview[],
      c12: [] as ChainMemberPreview[],
      other: [] as ChainMemberPreview[],
    };
    for (const m of payload.members) {
      const k = classKey(m.license_type);
      out[k].push(m);
    }
    return out;
  }, [payload.members]);

  const toggle = (lic: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lic)) next.delete(lic); else next.add(lic);
      return next;
    });
  };
  const toggleClass = (cls: "c10" | "c9" | "c12" | "other", check: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of grouped[cls]) {
        if (check) next.add(m.license_number);
        else next.delete(m.license_number);
      }
      return next;
    });
  };

  const inTerritoryCount = useMemo(
    () => payload.members.filter((m) => m.in_territory).length,
    [payload.members]
  );
  const outOfTerritoryCount = payload.members.length - inTerritoryCount;

  const runConfirm = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await onConfirmChain(row, payload, Array.from(selected));
    } finally {
      setBusy(false);
    }
  };

  const runNotARetailer = async () => {
    setBusy(true);
    try {
      await onNotARetailer(row);
    } finally {
      setBusy(false);
      setConfirmingNotRetailer(false);
    }
  };

  const renderClassSection = (cls: "c10" | "c9" | "c12", subheader: string) => {
    const list = grouped[cls];
    if (list.length === 0) return null;
    const allChecked = list.every((m) => selected.has(m.license_number));
    return (
      <div key={cls} className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <h5 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {subheader} <span className="text-gray-400">({list.length})</span>
          </h5>
          <button
            type="button"
            onClick={() => toggleClass(cls, !allChecked)}
            className="text-[11px] text-accent-700 hover:underline"
          >
            {allChecked ? "Deselect all" : "Select all"}
          </button>
        </div>
        <ul className="space-y-2">
          {list.map((m) => {
            const checked = selected.has(m.license_number);
            const dim = !m.in_territory;
            const inactive = !m.is_active;
            return (
              <li key={m.license_number} className={dim ? "opacity-60" : ""}>
                <LicenseRow
                  license={{
                    license_number: m.license_number,
                    gbp_display_name: m.gbp_display_name ?? m.display_name ?? null,
                    dba_name: m.dba_name,
                    legal_name: m.legal_name,
                    premise_city: m.premise_city,
                    premise_zip: m.premise_zip ?? null,
                  }}
                  leading={
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(m.license_number)}
                      aria-label={`include ${m.license_number}`}
                    />
                  }
                  trailing={
                    <div className="flex flex-col items-end gap-1">
                      {!m.in_territory && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                          out of territory
                        </span>
                      )}
                      {inactive && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                          inactive
                        </span>
                      )}
                    </div>
                  }
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const cmName = row.name || row.parent_name || "(unnamed)";

  return (
    <article className="rounded-lg border border-amber-200 bg-white p-5 shadow-sm">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-sm">
          <span className="font-medium text-gray-700">chain_match</span>
          {row.confidence != null && (
            <span className="text-gray-400">conf {Number(row.confidence).toFixed(2)}</span>
          )}
        </div>
        <Badge variant="review">CHAIN MATCH</Badge>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="rounded-md border border-gray-200 bg-gray-50/60 p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">From your file</h4>
          <dl className="space-y-1.5">
            <div>
              <dt className="text-xs text-gray-500">Name</dt>
              <dd className="text-base font-semibold text-gray-900">{cmName}</dd>
            </div>
            {row.cm_license && (
              <div>
                <dt className="text-xs text-gray-500">License</dt>
                <dd className="text-sm text-gray-800">{row.cm_license}</dd>
              </div>
            )}
            {row.city && (
              <div>
                <dt className="text-xs text-gray-500">City</dt>
                <dd className="text-sm text-gray-800">{row.city}</dd>
              </div>
            )}
            {row.website && (
              <div>
                <dt className="text-xs text-gray-500">Website</dt>
                <dd className="text-sm text-gray-800 break-all">{row.website}</dd>
              </div>
            )}
            {row.territory && (
              <div>
                <dt className="text-xs text-gray-500">Territory</dt>
                <dd className="text-sm text-gray-800">{row.territory}</dd>
              </div>
            )}
          </dl>
        </section>

        <section className="rounded-md border border-amber-200 bg-amber-50/30 p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Approved chain</h4>
          <dl className="space-y-1.5">
            <div>
              <dt className="text-xs text-gray-500">Chain</dt>
              <dd className="text-base font-semibold text-gray-900">{payload.display_name}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Stem</dt>
              <dd className="text-sm text-gray-800">{payload.canonical_root}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Members</dt>
              <dd className="text-sm text-gray-800">
                <span className="font-medium">{payload.member_count}</span>
                <span className="text-gray-500">
                  {" · "}{payload.member_breakdown.c10} C10
                  {" · "}{payload.member_breakdown.c9} C9
                  {" · "}{payload.member_breakdown.c12} C12
                </span>
              </dd>
            </div>
            {payload.website && (
              <div>
                <dt className="text-xs text-gray-500">Website</dt>
                <dd className="text-sm text-gray-800 break-all">{payload.website}</dd>
              </div>
            )}
            {row.territory && (
              <div>
                <dt className="text-xs text-gray-500">In territory</dt>
                <dd className="text-sm text-gray-800">
                  <span className="font-medium">{inTerritoryCount}</span>
                  <span className="text-gray-500"> of {payload.member_count}</span>
                  {outOfTerritoryCount > 0 && (
                    <span className="text-gray-400"> · {outOfTerritoryCount} out</span>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </section>
      </div>

      <details className="mt-3 group" open>
        <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
          <span className="inline-block transition-transform group-open:rotate-90">▸</span>
          <span>Why we think this is a chain match</span>
        </summary>
        <ul className="mt-2 ml-4 list-disc text-xs text-gray-600 space-y-1">
          <li>{triggerExplain(payload)}</li>
          <li>
            All {payload.member_count} member license{payload.member_count === 1 ? "" : "s"} of '
            <code className="text-gray-800">{payload.display_name}</code>' are presented below.
          </li>
          {row.territory && outOfTerritoryCount > 0 && (
            <li>
              {outOfTerritoryCount} member{outOfTerritoryCount === 1 ? " is" : "s are"} outside your declared territory and unchecked by default.
            </li>
          )}
          {payload.trigger === "name_canonical_root" && (
            <li className="text-amber-700">⚠ Name-only trigger — verify before confirming.</li>
          )}
        </ul>
      </details>

      <section className="mt-4 rounded-md border border-gray-200 bg-white p-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Member licenses</h4>
        <div className="max-h-80 overflow-y-auto pr-1">
          {renderClassSection("c10", "STOREFRONT (C10)")}
          {renderClassSection("c9", "DELIVERY (C9)")}
          {renderClassSection("c12", "MICROBUSINESS (C12)")}
        </div>
        <p className="mt-2 text-[11px] text-gray-500">
          {selected.size} of {payload.member_count} selected
        </p>
      </section>

      <div className="mt-4 flex flex-wrap gap-2">
        <Tooltip content="Confirm matches for the selected member licenses. Writes one link per member, all grouped under one undoable action.">
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={runConfirm}
            disabled={busy || selected.size === 0}
          >
            {busy ? "Confirming…" : `Confirm ${selected.size} match${selected.size === 1 ? "" : "es"}`}
          </Button>
        </Tooltip>
        <Tooltip content="None of the chain members are right — pick a single different retailer instead.">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => setPicking((p) => !p)}
            disabled={busy}
          >
            {picking ? "Close search" : "Pick different retailer"}
          </Button>
        </Tooltip>
        <Tooltip content="This row is a delivery partner, not a retailer we sell to directly.">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => onMarkDeliveryPartner(row)}
            disabled={busy}
          >
            Delivery partner
          </Button>
        </Tooltip>
        <Tooltip content="This row isn't a retailer at all (e.g. a B2B account). Removes it from the queue.">
          <Button
            variant="danger-ghost"
            size="sm"
            type="button"
            onClick={() => setConfirmingNotRetailer(true)}
            disabled={busy}
          >
            Not a retailer
          </Button>
        </Tooltip>
        <Tooltip content="Skip — leave for later, no state change.">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => onSkip(row)}
            disabled={busy}
          >
            Skip
          </Button>
        </Tooltip>
      </div>

      {picking && (
        <div className="mt-3">
          <CandidatePicker
            onPick={async (lic) => {
              setBusy(true);
              try {
                await onPickDifferent(row, lic);
                setPicking(false);
              } finally {
                setBusy(false);
              }
            }}
          />
        </div>
      )}

      <Dialog open={confirmingNotRetailer} onOpenChange={(v) => !busy && setConfirmingNotRetailer(v)}>
        <DialogTitle>Mark as not a retailer?</DialogTitle>
        <DialogDescription>
          This will remove the row from the review queue and record it as not-a-retailer. You can undo from the toast.
        </DialogDescription>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => setConfirmingNotRetailer(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="button" onClick={runNotARetailer} disabled={busy}>
            {busy ? "Marking…" : "Yes, not a retailer"}
          </Button>
        </div>
      </Dialog>
    </article>
  );
}
